import { useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { MoreVertical, Pencil, Plus, Loader2 } from 'lucide-react'
import { usePacks, usePendingFacilityUpdates, useUpdateFacility } from '@/hooks/useBanking'
import { useFacilitiesPage, useFacilityBankAccountCounts } from '@/hooks/useFacilitiesPage'
import { SEARCH_DEBOUNCE_MS, useDebouncedValue } from '@/hooks/useDebouncedValue'
import { AddFacilityDialog } from '@/components/banking/AddFacilityDialog'
import { EditFacilityDialog } from '@/components/banking/EditFacilityDialog'
import type { Facility } from '@/types/banking'
import { financialAccountsBasePath } from '@/components/financial-accounts/financialAccountsScope'
import { FacilitiesFilters } from '@/components/banking/FacilitiesFilters'
import { FacilityStatusToggle } from '@/components/banking/FacilityStatusToggle'
import type { FacilitiesFilterState } from '@/components/banking/FacilitiesFilters'
import { useTablePagination } from '@/hooks/useTablePagination'
import { queryErrorDisplay, tableQueryState } from '@/components/common/queryErrorDisplay'
import {
  Button,
  Badge,
  DataTable,
  type DataTableColumn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  PageHeader,
  toast,
} from '@atrvd/saas-ui'

const NO_FACILITIES: Facility[] = []

/**
 * Renders one facility's bank-account count.
 *
 * The state is driven by whether the count is KNOWN, not by a loading flag:
 * `count === null` means "not known yet" and renders a spinner, so "0" only
 * ever means "this facility has no bank accounts" — never a placeholder for a
 * count that has not loaded.
 *
 * @param count - Bank accounts linked to the facility, or null if not yet known.
 * @param unavailableReason - Why no number can be shown, or null when it can.
 *   Doubles as the cell's tooltip so the dash is never unexplained.
 */
function BankAccountCountCell({
  count,
  unavailableReason,
}: {
  count: number | null
  unavailableReason: string | null
}) {
  if (unavailableReason !== null) {
    // `title` alone is not an accessible name: screen readers announce it
    // inconsistently and touch devices never surface it without a long press.
    // The dash is decorative once the reason is exposed as real text, so it is
    // hidden from assistive tech and the reason is carried by an sr-only span —
    // the same treatment the loading state gets below.
    return (
      <span className="text-muted-foreground" title={unavailableReason}>
        <span aria-hidden="true">—</span>
        <span className="sr-only">{unavailableReason}</span>
      </span>
    )
  }

  if (count === null) {
    return (
      <>
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        <span className="sr-only">Loading bank account count</span>
      </>
    )
  }

  return <span>{count}</span>
}

/**
 * Settings page showing list of facilities in a table format
 */
export function SettingsFacilitiesPage() {
  const navigate = useNavigate()
  // Add dialog state — single open flag mirroring the EditFacilityDialog
  // contract; the dialog owns its own form via react-hook-form, so the
  // page no longer has to manage form/validation state itself.
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  // Edit dialog state — single source of truth for both the open flag and the
  // currently-edited facility. `editingFacility` is cleared on close so the
  // form doesn't flash stale data the next time the dialog opens for a
  // different row.
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editingFacility, setEditingFacility] = useState<Facility | null>(null)

  const [filters, setFilters] = useState<FacilitiesFilterState>({
    search: '',
    status: 'all',
    packId: 'all',
  })
  // The box stays bound to `filters.search`; the server sees the settled term.
  const search = useDebouncedValue(filters.search.trim(), SEARCH_DEBOUNCE_MS)

  const { mutateAsync: updateFacility } = useUpdateFacility()
  const pendingFacilityUpdates = usePendingFacilityUpdates()

  // `mutateAsync`, not `mutate` with callbacks: a second `mutate` re-points the
  // observer, so the first write's per-call callbacks would never run.
  // Stable so the memoized columns below are not rebuilt on every render.
  const setFacilityActive = useCallback(
    async (facility: Facility, isActive: boolean) => {
      try {
        const result = await updateFacility({ id: facility.id, data: { is_active: isActive } })
        // Unreachable for an is_active-only write (no unique column changes), but
        // a 'duplicate' result must never read as success.
        if (result.status !== 'saved') throw new Error('Failed to update facility status')
        toast.success(`${facility.name} is now ${isActive ? 'active' : 'inactive'}`)
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Failed to update facility status')
      }
    },
    [updateFacility],
  )

  // Any filter change goes back to page 1. The total comes back with the page,
  // so the footer (and the clamp when a toggle under a status filter empties
  // the last page) goes through `forTotal` after the page query.
  const pagination = useTablePagination({
    totalItems: undefined,
    resetKey: JSON.stringify([search, filters.status, filters.packId]),
  })
  // Management page: lists ALL facilities, active and inactive, so users can
  // see and toggle deactivated rows.
  const facilitiesQuery = useFacilitiesPage({
    search,
    status: filters.status,
    packId: filters.packId,
    page: pagination.page,
    pageSize: pagination.pageSize,
  })
  const { data } = facilitiesQuery
  const { dataTablePagination } = pagination.forTotal({
    total: data?.total,
    isFetching: facilitiesQuery.isFetching,
  })
  const facilities = data?.items ?? NO_FACILITIES
  const tableState = tableQueryState(facilitiesQuery, facilities.length)
  const errorDisplay = queryErrorDisplay(tableState, facilities.length)

  // All packs: the pack filter's options, and the name shown in the Pack column.
  const { data: packs } = usePacks()
  const packNameById = useMemo(
    () => new Map((packs ?? []).map((p) => [p.id, p.name])),
    [packs],
  )

  // The "Bank Accounts" column counts every non-deleted account of the row's
  // facility — manual and connected, including an orphan whose connection is
  // gone. The money tiles count a narrower set (cash-eligible accounts under
  // active facilities); this page is where the rows the rest of the product
  // has stopped counting stay visible, so do not narrow it to match them.
  const { data: accountCounts, error: accountCountsError } = useFacilityBankAccountCounts(
    facilities.map((facility) => facility.id),
  )
  // A refetch that fails on top of cached counts keeps showing them.
  const accountCountsUnavailable =
    accountCountsError && !accountCounts ? 'Failed to load bank accounts' : null

  const columns = useMemo<DataTableColumn<Facility>[]>(
    () => [
      {
        key: 'sage_location_id',
        header: 'Sage Location ID',
        render: (facility) => facility.sage_location_id || '-',
        // A short code: wrapping splits it at the hyphen ("DEMO-LOC-" / "13").
        cellClassName: 'font-mono whitespace-nowrap',
      },
      { key: 'legal_name', header: 'Facility Legal Name', render: (facility) => facility.legal_name || '-' },
      {
        key: 'name',
        header: 'Display Name',
        render: (facility) => (
          <button
            onClick={() => navigate(financialAccountsBasePath({ facilityId: facility.id }))}
            className="text-left hover:underline font-medium"
          >
            {facility.name}
          </button>
        ),
      },
      {
        key: 'source',
        header: 'Source',
        // Sage-imported vs. manually added. A badge never wraps its two words.
        cellClassName: 'whitespace-nowrap',
        render: (facility) => (
          <Badge
            variant="outline"
            className={
              facility.source === 'sage_intacct'
                ? 'bg-blue-50 text-blue-700 border-blue-200'
                : 'bg-gray-50 text-gray-700 border-gray-200'
            }
          >
            {facility.source === 'sage_intacct' ? 'Sage Intacct' : 'Manual'}
          </Badge>
        ),
      },
      {
        key: 'pack_id',
        header: 'Pack',
        render: (facility) => (facility.pack_id && packNameById.get(facility.pack_id)) || '-',
      },
      {
        key: 'bank_accounts',
        header: 'Bank Accounts',
        render: (facility) => (
          <BankAccountCountCell
            count={accountCounts?.[facility.id] ?? null}
            unavailableReason={accountCountsUnavailable}
          />
        ),
      },
      {
        key: 'is_active',
        header: 'Status',
        render: (facility) => (
          <FacilityStatusToggle
            facilityName={facility.name}
            sageLocationId={facility.sage_location_id}
            // A write in flight shows the value it sends, so the click
            // registers at once; once settled the stored value shows.
            active={pendingFacilityUpdates.get(facility.id)?.is_active ?? facility.is_active}
            busy={pendingFacilityUpdates.has(facility.id)}
            onChange={(isActive) => void setFacilityActive(facility, isActive)}
          />
        ),
      },
      {
        key: 'actions',
        header: 'Actions',
        width: '50px',
        render: (facility) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => {
                  setEditingFacility(facility)
                  setEditDialogOpen(true)
                }}
              >
                <Pencil className="mr-2 h-4 w-4" /> Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(financialAccountsBasePath({ facilityId: facility.id }))}>
                Manage Financial Accounts
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [
      navigate,
      packNameById,
      accountCounts,
      accountCountsUnavailable,
      pendingFacilityUpdates,
      setFacilityActive,
    ],
  )

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Facilities (${data?.total ?? 0})`}
        description="Manage your organization's facilities"
        actions={
          <Button className="bg-red-700 hover:bg-red-800" onClick={() => setAddDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Facility
          </Button>
        }
      />

      <FacilitiesFilters filters={filters} onFiltersChange={setFilters} packs={packs ?? []} />

      {errorDisplay.banner}
      <DataTable
        columns={columns}
        data={facilities}
        loading={tableState.loading}
        refreshing={tableState.refreshing}
        error={errorDisplay.tableError}
        rowKey={(facility) => facility.id}
        itemName="facilities"
        pagination={dataTablePagination}
        emptyState={
          <p className="text-center text-muted-foreground">
            {/* Show a filter-specific message when any predicate is active */}
            {filters.search.trim() || filters.status !== 'all' || filters.packId !== 'all'
              ? 'No facilities match your filters'
              : 'No facilities found'}
          </p>
        }
      />

      {/* Add Facility Dialog — mirrors the Edit dialog shape (react-hook-form
          + shared facilityFormSchema) so the two flows can't drift on
          validation / field set / UX behaviour. */}
      <AddFacilityDialog open={addDialogOpen} onOpenChange={setAddDialogOpen} />

      {/* Edit Facility Modal */}
      <EditFacilityDialog
        open={editDialogOpen}
        onOpenChange={(o) => {
          setEditDialogOpen(o)
          // Clear the selected facility on close so the next open starts fresh.
          if (!o) setEditingFacility(null)
        }}
        facility={editingFacility}
      />
    </div>
  )
}
